import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, CalendarDays, CheckCircle2, Sparkles, X } from '../components/icons';
import Button from '../components/Button';
import Card from '../components/Card';
import Input from '../components/Input';
import Modal from '../components/Modal';
import { crmService } from '../services/crmService';
import { useAuth } from '../store/authContext';
import type { AgendaItem } from '../types';

const weekDays = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const LOADING_STEPS = [
  'Conectando agenda com o painel comercial...',
  'Coletando reuniões e sincronizando horários...',
  'Organizando compromissos por dia e prioridade...',
  'Finalizando visualização da agenda executiva...'
];

const pad = (n: number) => String(n).padStart(2, '0');
const dateKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toBrDate = (iso: string) => {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
};

const monthLabel = (d: Date) =>
  new Intl.DateTimeFormat('pt-BR', {
    month: 'long',
    year: 'numeric'
  }).format(d);

const buildMonthCells = (ref: Date) => {
  const y = ref.getFullYear();
  const m = ref.getMonth();
  const first = new Date(y, m, 1);
  const last = new Date(y, m + 1, 0);
  const startOffset = (first.getDay() + 6) % 7;
  const cells: Array<Date | null> = [];
  for (let i = 0; i < startOffset; i += 1) cells.push(null);
  for (let day = 1; day <= last.getDate(); day += 1) cells.push(new Date(y, m, day));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

export default function AgendaPage() {
  const { session } = useAuth();
  const [items, setItems] = useState<AgendaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingStep, setLoadingStep] = useState(0);
  const [monthRef, setMonthRef] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(dateKey(new Date()));
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({ titulo: '', data: dateKey(new Date()), hora: '10:00', descricao: '' });

  const load = async () => {
    setLoading(true);
    try {
      const agenda = await crmService.getAgenda();
      setItems(agenda);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!loading) {
      setLoadingStep(0);
      return;
    }
    const timer = window.setInterval(() => {
      setLoadingStep((prev) => (prev + 1) % LOADING_STEPS.length);
    }, 1300);
    return () => window.clearInterval(timer);
  }, [loading]);

  const byDay = useMemo(() => {
    return items.reduce<Record<string, AgendaItem[]>>((acc, item) => {
      if (!acc[item.data]) acc[item.data] = [];
      acc[item.data].push(item);
      return acc;
    }, {});
  }, [items]);

  const monthCells = useMemo(() => buildMonthCells(monthRef), [monthRef]);
  const selectedItems = byDay[selectedDate] || [];
  const yearOptions = useMemo(() => {
    const y = new Date().getFullYear();
    return Array.from({ length: 7 }).map((_, i) => y - 3 + i);
  }, []);

  const goPrevMonth = () => setMonthRef((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  const goNextMonth = () => setMonthRef((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  const goToday = () => {
    const now = new Date();
    setMonthRef(new Date(now.getFullYear(), now.getMonth(), 1));
    setSelectedDate(dateKey(now));
  };

  const create = async () => {
    if (!session?.user.id || !form.titulo || !form.data || !form.hora) return;
    await crmService.createAgenda({
      user_id: session.user.id,
      titulo: form.titulo,
      data: form.data,
      hora: form.hora,
      descricao: form.descricao
    });
    setForm({ titulo: '', data: form.data, hora: '10:00', descricao: '' });
    setOpenCreate(false);
    setSelectedDate(form.data);
    setMonthRef(new Date(`${form.data}T00:00:00`));
    await load();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-black">Agenda</h1>
        <div className="flex items-center gap-2">
          <Button className="h-9 px-3 text-xs" variant="primary" onClick={() => setOpenCreate(true)} iconLeft={<CalendarDays />}>
            Novo compromisso
          </Button>
          <select
            value={monthRef.getMonth()}
            onChange={(e) => {
              const next = new Date(monthRef.getFullYear(), Number(e.target.value), 1);
              setMonthRef(next);
              setSelectedDate(dateKey(next));
            }}
            className="h-10 rounded-xl border border-borderLight bg-cardLight px-3 text-sm dark:border-borderDark dark:bg-cardDark"
          >
            {['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'].map((label, idx) => (
              <option key={label} value={idx}>
                {label}
              </option>
            ))}
          </select>
          <select
            value={monthRef.getFullYear()}
            onChange={(e) => {
              const next = new Date(Number(e.target.value), monthRef.getMonth(), 1);
              setMonthRef(next);
              setSelectedDate(dateKey(next));
            }}
            className="h-10 rounded-xl border border-borderLight bg-cardLight px-3 text-sm dark:border-borderDark dark:bg-cardDark"
          >
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      <Card className="overflow-hidden p-0">
          {loading ? (
            <div className="google-search-loading-card m-3 space-y-5 overflow-hidden rounded-2xl">
              <div className="google-search-grid" />
              <div className="google-search-scanline" />
              <div className="google-search-progress" />
              <div className="flex flex-col items-center gap-4 py-2 text-center">
                <div className="google-search-scene">
                  <div className="google-search-beam" />
                  <div className="google-search-ring google-search-ring-a" />
                  <div className="google-search-ring google-search-ring-b" />
                  <div className="google-search-orbit google-search-orbit-inner" />
                  <div className="google-search-orbit google-search-orbit-outer" />
                  <div className="google-search-core">
                    <Sparkles className="h-5 w-5" />
                  </div>
                </div>
                <div>
                  <p className="google-search-title text-sm font-bold text-slate-800 dark:text-slate-100">Coletando reuniões da Agenda</p>
                  <p className="text-xs text-slate-600 dark:text-slate-300">{LOADING_STEPS[loadingStep]}</p>
                </div>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {LOADING_STEPS.map((step, index) => (
                  <div
                    key={step}
                    className={`google-search-step rounded-xl border px-3 py-2 text-xs transition-all duration-300 ${
                      index === loadingStep
                        ? 'google-search-step-active border-primary/50 bg-primary/10 text-slate-900 dark:border-cta/50 dark:bg-cta/15 dark:text-slate-100'
                        : 'border-borderLight/70 bg-white/40 text-slate-500 dark:border-borderDark/70 dark:bg-slate-900/20 dark:text-slate-400'
                    }`}
                  >
                    {step}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {!loading ? (
            <>
          <div className="flex items-center justify-between border-b border-borderLight px-4 py-3 dark:border-borderDark">
          <div className="flex items-center gap-2">
              <Button className="h-8 px-2 text-xs" variant="outline" onClick={goPrevMonth} iconLeft={<ArrowRight className="rotate-180" />}>
                Anterior
              </Button>
              <Button className="h-8 px-2 text-xs" variant="outline" onClick={goNextMonth} iconLeft={<ArrowRight />}>
                Próximo
              </Button>
              <Button className="h-8 px-2 text-xs" variant="outline" onClick={goToday}>
                Hoje
              </Button>
            </div>
            <p className="text-lg font-bold capitalize">{monthLabel(monthRef)}</p>
          </div>

          <div className="grid grid-cols-7 border-b border-borderLight dark:border-borderDark">
            {weekDays.map((d) => (
              <div key={d} className="px-2 py-2 text-center text-xs font-semibold text-slate-500">
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {monthCells.map((date, idx) => {
              if (!date) return <div key={`n-${idx}`} className="h-28 border-b border-r border-borderLight bg-slate-50/60 dark:border-borderDark dark:bg-slate-900/30" />;
              const key = dateKey(date);
              const dayEvents = (byDay[key] || []).sort((a, b) => a.hora.localeCompare(b.hora));
              const active = key === selectedDate;
              const today = key === dateKey(new Date());
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelectedDate(key)}
                  className={`h-28 border-b border-r p-2 text-left ${active ? 'bg-primary/10' : 'hover:bg-slate-50 dark:hover:bg-slate-900/40'} border-borderLight dark:border-borderDark`}
                >
                  <div className="mb-1 flex items-center justify-between">
                    <span className={`inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full text-xs font-semibold ${today ? 'bg-primary text-white' : ''}`}>
                      {date.getDate()}
                    </span>
                    {dayEvents.length ? <span className="rounded-full bg-cta/15 px-1.5 py-0.5 text-[10px] font-semibold text-green-700 dark:text-green-300">{dayEvents.length}</span> : null}
                  </div>
                  <div className="space-y-1">
                    {dayEvents.slice(0, 3).map((ev) => (
                      <p key={ev.id} className="truncate rounded bg-slate-100 px-1.5 py-0.5 text-[10px] dark:bg-slate-800">
                        {ev.hora} {ev.titulo}
                      </p>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
            </>
          ) : null}
      </Card>

      <Card className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="font-semibold">Compromissos do dia</p>
          <p className="text-xs text-slate-500">{toBrDate(selectedDate)}</p>
        </div>
        {selectedItems.length === 0 ? <p className="text-sm text-slate-500">Nenhum compromisso nessa data.</p> : null}
        {selectedItems
          .sort((a, b) => a.hora.localeCompare(b.hora))
          .map((item) => (
            <div key={item.id} className="flex items-start justify-between gap-3 rounded-xl border border-borderLight p-3 dark:border-borderDark">
              <div>
                <p className="font-semibold">{item.titulo}</p>
                <p className="text-xs text-slate-500">
                  {toBrDate(item.data)} · {item.hora} · {item.descricao || 'Sem descrição'}
                </p>
              </div>
              <Button
                variant="outline"
                className="h-8 px-2 text-xs"
                iconLeft={<X />}
                onClick={async () => {
                  await crmService.deleteAgenda(item.id);
                  await load();
                }}
              >
                Remover
              </Button>
            </div>
          ))}
      </Card>

      <Modal open={openCreate} onClose={() => setOpenCreate(false)} title="Novo compromisso">
        <div className="space-y-3">
          <Input label="Título" value={form.titulo} onChange={(e) => setForm((p) => ({ ...p, titulo: e.target.value }))} />
          <div className="grid grid-cols-2 gap-3">
            <Input type="date" label="Data" value={form.data} onChange={(e) => setForm((p) => ({ ...p, data: e.target.value }))} />
            <Input type="time" label="Hora" value={form.hora} onChange={(e) => setForm((p) => ({ ...p, hora: e.target.value }))} />
          </div>
          <Input label="Descrição" value={form.descricao} onChange={(e) => setForm((p) => ({ ...p, descricao: e.target.value }))} />
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setOpenCreate(false)} iconLeft={<X />}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={create} iconLeft={<CheckCircle2 />}>
              Salvar compromisso
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
